<template>
  <b-row>
    <b-col
      class="container__uploader"
      :class="{
        // 'is-processing': isProcessing,
        'is-disabled': disabled,
        'hide-dropzone': hideDropzone,
        'hide-remove': hideRemove
      }"
    >
      <div v-if="showMaxFilesize" class="text-black-50 mb-2">
        (Supported all file types up to {{ maxFilesize }} MB)
      </div>

      <vue-dropzone
        :id="id"
        ref="vueDropzone"
        :key="dropzoneKey"
        :options="$_dropzoneOptions"
        :awss3="$_s3Options"
        v-bind="{ ...$attrs }"
        @vdropzone-files-added="onFilesAdded"
        @vdropzone-removed-file="onRemovedFile"
        @vdropzone-success="onSuccess"
        @vdropzone-queue-complete="onQueueComplete"
        @vdropzone-complete="onComplete"
        @vdropzone-error="onError"
        @vdropzone-thumbnail="onThumbnailCreated"
        v-on="{ ...$_customListeners, ...$listeners }"
      />
      <small
        v-if="!hideErrorMessage && isErrorFileType"
        style="display: inline; color: red; margin-top: 0"
        >Please upload files of the following type: {{ $_dropzoneOptions.acceptedFiles }}</small
      >

      <small
        v-if="!hideErrorMessage && isErrorFileSize"
        style="display: inline; color: red; margin-top: 0"
      >
        File is too big. Max filesize: {{ $_dropzoneOptions.maxFilesize }}MB.
      </small>
    </b-col>
  </b-row>
</template>

<script>
import { useFileSizeConverter } from '@/composables/useFileSizeConverter';
import { amplitudeMixin } from '@/mixins/amplitudeMixin';
import { dropzoneMixin } from '@/mixins/dropzoneMixin';
import Upload from '@/models/Upload';
import Auth from '@/models/Auth';
import vue2Dropzone from 'vue2-dropzone';
import { useAuth } from '@/composables/useAuth';

const { refreshToken } = useAuth();
const { formatBytes } = useFileSizeConverter();

const findByAttributes = (file, removedFile) => {
  return (
    file.name === removedFile.name &&
    file.size === removedFile.size &&
    file.lastModifiedDate.toString() === removedFile.lastModifiedDate.toString()
  );
};

// remove new selected file
const findByUploadUUid = (file, removedFile) => {
  return file.upload && removedFile.upload && file.upload.uuid === removedFile.upload.uuid;
};

// remove previous uploaded file
const findById = (file, removedFile) => {
  return file.id === removedFile.id;
};
export default {
  components: {
    vueDropzone: vue2Dropzone
  },
  mixins: [amplitudeMixin, dropzoneMixin],

  model: {
    prop: 'files',
    event: 'change'
  },

  props: {
    id: {
      type: String,
      required: true
    },

    s3Options: {
      type: Object,
      default: () => ({})
    },

    dropzoneOptions: {
      type: Object,
      default: () => ({})
    },

    files: {
      type: Array,
      default: () => []
    },

    referenceMeta: {
      type: Object,
      default: () => ({
        ref: '',
        refId: '',
        field: ''
      })
    },

    disabled: {
      type: Boolean,
      default: false
    },

    showCover: {
      type: Boolean,
      default: false
    },

    autoUpload: {
      type: Boolean,
      default: false
    },

    hideDropzone: {
      type: Boolean,
      default: false
    },

    hideRemove: {
      type: Boolean,
      default: false
    },

    maxFilesize: {
      type: Number,
      default: 512
    },

    showMaxFilesize: {
      type: Boolean,
      default: false
    },

    deleteable: {
      type: Boolean,
      default: true
    },

    hideErrorMessage: {
      type: Boolean,
      default: false
    },

    isPublic: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      buffer: [],
      deletedIds: [],
      promiseResolve: null,
      promiseReject: null,
      isErrorFileType: false,
      isErrorFileSize: false
      // isProcessing: false,
    };
  },

  computed: {
    $_s3Options() {
      return {
        signingURL: `${process.env.VUE_APP_BASE_URL}/upload/s3/presigned-post`,
        headers: {
          Authorization: `Bearer ${Auth.getAppToken()}`
        },
        params: {},
        sendFileToServer: false,
        withCredentials: false,
        ...this.s3Options
      };
    },

    dropzoneKey() {
      // Create a key that changes when files content changes
      return this.files.map((file) => `${file.id || ''}-${file.name}-${file.size}`).join('|');
    },

    $_dropzoneOptions() {
      const customOptions = {};

      if (this.showCover) {
        this.$set(customOptions, 'addRemoveLinks', false);
      }

      return {
        url: `${process.env.VUE_APP_BASE_URL}/upload`,
        autoProcessQueue: this.autoUpload,
        addRemoveLinks: this.deleteable,
        uploadMultiple: false,
        timeout: 99999999, //can't set timeout is null because work on milliseconds.
        maxFilesize: this.maxFilesize, //now type is MB. It's not bytes.
        init: () => {
          this.$nextTick(() => {
            // Initialize files from props first
            if (this.$_files.length) {
              const { maxFiles = null } = this.$_dropzoneOptions;

              this.$_files.forEach((attachment) => {
                this.manuallyAddFile(attachment);
              });

              if (maxFiles !== null && this.$_files.length === maxFiles) {
                const dropzoneElement = this.$refs.vueDropzone.$el;

                if (!dropzoneElement.classList.contains('dz-max-files-reached')) {
                  dropzoneElement.classList.add('dz-max-files-reached');
                }
              }
            }

            // Initialize buffer files after a small delay to ensure proper rendering at the bottom of the list
            setTimeout(() => {
              if (this.buffer.length) {
                this.buffer.forEach((bufferFile) => {
                  if (this.$_isValidAddedFile(bufferFile)) {
                    this.$refs.vueDropzone.dropzone.addFile(bufferFile);
                  }
                });
              }
            }, 100);
          });
        },
        ...customOptions,
        ...this.dropzoneOptions
      };
    },

    $_customListeners() {
      const customListeners = {};

      if (this.showCover) {
        this.$set(customListeners, 'vdropzone-thumbnail', this.thumbnail);
      }

      return customListeners;
    },

    $_files: {
      get() {
        return this.files;
      },

      set(v) {
        this.$emit('change', v);
      }
    },

    $_formatedMaxFilesize() {
      return formatBytes(this.maxFilesize);
    },

    totalQueuedFiles() {
      return this.buffer.filter((bufferItem) => bufferItem.status !== 'error').length;
    }
  },

  watch: {
    buffer: 'onBufferUpdated'
  },

  methods: {
    coverTemplate() {
      return `<div class="dz-preview dz-cover-preview">
                <div class="dz-image-cover">
                    <div data-dz-thumbnail-cover></div>
                </div>

                <div class="dz-cover-details">
                  <div class="dz-details">
                      <div class="dz-size"><span data-dz-size></span></div>
                      <div class="dz-filename"><span data-dz-name></span></div>
                  </div>
                  <div class="dz-progress"><span class="dz-upload" data-dz-uploadprogress></span></div>
                  <div class="dz-error-message"><span data-dz-errormessage></span></div>
                  <div class="dz-success-mark"><i class="fa fa-check"></i></div>
                  <div class="dz-error-mark"><i class="fa fa-close"></i></div>
                  <button class="dz-remove btn btn-link" data-dz-remove>
                    <i class="fa fa-close"></i>
                  </button>
                </div>
                
            </div>
        `;
    },

    async thumbnail(file) {
      var j, len, ref, thumbnailElement;

      if (file && file.previewElement) {
        file.previewElement.classList.remove('dz-file-preview');

        ref = file.previewElement.querySelectorAll('[data-dz-thumbnail]');

        for (j = 0, len = ref.length; j < len; j++) {
          thumbnailElement = ref[j];
          thumbnailElement.alt = file.name;

          if (file.id) {
            const { response } = await Upload.api().fetchPresignedUrl(file.id);

            thumbnailElement.src = response.data.url;
          } else {
            thumbnailElement.style.backgroundImage = 'url("' + URL.createObjectURL(file) + '")';
          }
        }
        return setTimeout(
          (function () {
            return function () {
              return file.previewElement.classList.add('dz-image-preview');
            };
          })(this),
          1
        );
      }
    },

    manuallyAddFile(attachment) {
      const { id, size, name, ext, url } = attachment;

      const file = { size, name, type: ext, id, url };

      this.$nextTick(() => {
        if (this.$refs.vueDropzone) {
          this.$refs.vueDropzone.manuallyAddFile(file, url);
        }
      });
    },

    onBufferUpdated(buffer) {
      this.$emit(
        'buffer:updated',
        buffer.filter((bufferItem) => bufferItem.status !== 'error')
      );
    },

    onFilesAdded(files) {
      this.$nextTick(() => {
        const validatedFiles = [];

        [...files].forEach((file) => {
          if (this.$_isValidAddedFile(file)) {
            validatedFiles.push(file);
          } else {
            this.$refs.vueDropzone.removeFile(file);
          }
        });
        this.isErrorFileType = false;
        this.isErrorFileSize = false;

        this.$emit('onFilesAdded', validatedFiles);

        if (this.$_dropzoneOptions.autoProcessQueue) {
          // this.isProcessing = true;
        } else {
          this.buffer = [...this.buffer, ...validatedFiles];
        }
      });
    },

    $_isValidAddedFile(file) {
      return file && file.upload && file.upload.uuid !== undefined;
    },

    addFile(file) {
      this.$nextTick(() => {
        this.$refs.vueDropzone.dropzone.addFile(file);

        this.buffer = [...this.buffer, file];
      });
    },

    addFiles(fileList) {
      Array.from(fileList).forEach((file) => this.addFile(file));
    },

    openFileBrowser() {
      this.$nextTick(() => {
        this.$refs.vueDropzone.$el.click();
      });
    },

    hasAnyQueuedFiles() {
      return this.totalQueuedFiles > 0;
    },

    hasAnyDeletedFiles() {
      return this.deletedIds.length > 0;
    },

    $_findFileIndex(file) {
      const findMethod = file.id ? findById : file.upload ? findByUploadUUid : findByAttributes;

      const filePointer = file.id ? '$_files' : 'buffer';

      return this[filePointer].findIndex((f) => findMethod(f, file));
    },

    onRemovedFile(removedFile) {
      if (!this.deleteable) return;

      // Check if dropzone instance exists and is properly initialized
      const dropzoneInstance = this.$refs.vueDropzone?.dropzone;
      if (!dropzoneInstance || dropzoneInstance.disabled === true || removedFile.status === 'error')
        return;

      let removedFileIndex = this.$_findFileIndex(removedFile);

      if (removedFileIndex !== -1) {
        const filePointer = removedFile.id ? '$_files' : 'buffer';
        const removedFileArray = this[filePointer].splice(removedFileIndex, 1);

        if (removedFile.id) {
          if (this.$_dropzoneOptions.autoProcessQueue) {
            Upload.api().deleteFile(removedFile.id);
          } else {
            this.deletedIds.push(removedFile.id);
          }
        }

        if (removedFile.manuallyAdded) {
          const { maxFiles = null } = this.$_dropzoneOptions;

          if (maxFiles !== null && dropzoneInstance.options.maxFiles < maxFiles) {
            const dropzoneElement = this.$refs.vueDropzone.$el;

            if (dropzoneElement.classList.contains('dz-max-files-reached')) {
              dropzoneElement.classList.remove('dz-max-files-reached');
            }
          }
        }

        const { referenceMeta, amplitudeAttribute } = this;

        this.$_amplitudeMixin_onFileRemoved(removedFileArray[0], {
          referenceMeta,
          ...amplitudeAttribute
        });

        this.$emit('file:removed', removedFileArray[0]);
      }
    },

    async upload() {
      // this.isProcessing = true;
      await this.validateAndRefreshToken();

      const promise = new Promise((resolve, reject) => {
        this.promiseResolve = resolve;
        this.promiseReject = reject;
      });

      this.$nextTick(() => {
        this.$refs.vueDropzone.processQueue();
      });

      return promise;
    },

    async validateAndRefreshToken() {
      const currentToken = Auth.getAppToken();
      const isExpired = Auth.isTokenExpired();
      const expireDate = Auth.getExpireDateAppToken(); // current token expiry 5 minutes

      const refreshThreshold = 2 * 60 * 1000; // 2 minutes in milliseconds

      if (!currentToken || isExpired) {
        console.log('[BaseS3Uploader] Refreshing: no token or expired');

        await refreshToken();
        return;
      }

      // calculate time until expiry in milliseconds
      const timeLeftMs = expireDate.getTime() - Date.now();

      // If token expires in less than the threshold, refresh it
      if (timeLeftMs <= refreshThreshold) {
        console.log(`[BaseS3Uploader] Refreshing: token expiring soon'`);

        await refreshToken();
      } else {
        console.log(`[BaseS3Uploader] Using cached token`);
      }
    },

    async deleteRemovedFiles() {
      let promise;

      const { deletedIds = [] } = this;

      if (deletedIds) {
        promise = await Promise.all(
          deletedIds.map((deletedId) => Upload.api().deleteFile(deletedId))
        );
        this.deletedIds = [];
      }

      return promise;
    },

    onSuccess(file) {
      if (file.processing) {
        file.previewElement.querySelector('.dz-progress').remove();

        const { referenceMeta, amplitudeAttribute } = this;

        this.$_amplitudeMixin_onFileUploaded(file, {
          referenceMeta,
          ...amplitudeAttribute
        });
      }
    },

    onComplete(file) {
      if (file.processing) {
        file.previewElement.classList.remove('dz-processing');
      }

      this.$emit('onComplete', file);
    },

    async onQueueComplete() {
      console.log('onQueueComplete ');

      const { referenceMeta, buffer = [] } = this;

      const uploads = [];

      let targetFiles = this.$_dropzoneOptions.autoProcessQueue
        ? this.$refs.vueDropzone.getAcceptedFiles()
        : buffer;

      targetFiles = targetFiles.filter((bufferItem) => bufferItem.status !== 'error');

      for (let index = 0; index < targetFiles.length; index++) {
        const targetFile = targetFiles[index];

        const uploadPromise = await Upload.api().createS3Upload({
          ...referenceMeta,
          name: targetFile.name,
          size: targetFile.size,
          mime: targetFile.s3Signature.ContentType,
          url: targetFile.s3Url,
          key: targetFile.s3Signature.key,
          isPublic: this.isPublic
        });

        const { data: upload } = uploadPromise.response;

        uploads.push(upload);

        this.$set(targetFile, 'id', upload.id);

        this.$nextTick(() => {
          this.$refs.vueDropzone.removeFile(targetFile);
        });

        this.manuallyAddFile(targetFile); // important! don't change to use upload object, it will cause file duplicate!!!
      }

      const newFiles = [...this.$_files, ...uploads];

      this.$_files = [...newFiles]; // syncing file should be changed, syncing file may cause the uploader work improperly!!

      this.buffer = [];

      // this.isProcessing = false;

      this.$emit('upload:complete', uploads);

      if (this.promiseResolve) {
        this.promiseResolve(uploads);

        this.promiseResolve = null;
      }
    },

    onError(file, message) {
      this.isErrorFileType = this.$_dropzoneMixin_handleFileTypeError(
        this.$refs.vueDropzone,
        file,
        message
      );

      this.isErrorFileSize = this.$_dropzoneMixin_handleFileSizeError(
        this.$refs.vueDropzone,
        file,
        message,
        this.maxFilesize
      );
    },

    onThumbnailCreated(file) {
      const fileIndex = this.$_findFileIndex(file);

      if (fileIndex === -1) {
        file.previewElement.remove();
      }
    },
    resetUploader() {
      console.log('[BaseS3Uploader] resetUploader called');

      this.deletedIds = [];
      this.buffer = [];
      this.$refs.vueDropzone.removeAllFiles();
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .ql-editor {
  min-height: 10rem;

  resize: vertical;

  p {
    margin-bottom: 1rem;
  }
}

// ::v-deep .dz-default {
// }

::v-deep .container__uploader {
  &.is-processing,
  &.is-disabled {
    .dz-message {
      display: none !important;
    }

    .dz-remove {
      display: none !important;
    }
  }

  &.hide-remove {
    .dz-remove {
      display: none !important;
    }
  }

  &.hide-dropzone {
    .dz-message {
      display: none !important;
    }
  }
}

::v-deep .dz-preview {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-top: 10px;
  padding-left: 10px;
  padding-bottom: 10px;
  padding-bottom: 10px;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-bottom: 1px solid #eeeff8;

  &:hover {
    background-color: #f4f5fd;
    border: 0px solid transparent;
    border-radius: 10px;
  }

  &.dz-processing {
    padding-bottom: 25px !important;

    .dz-progress {
      display: flex !important;
    }

    .dz-remove {
      display: none !important;
    }
  }

  &.dz-complete {
    padding-bottom: 10px !important;
  }
}

::v-deep .dz-message {
  margin-bottom: 10px;
}

::v-deep .dz-progress {
  width: 100%;
  position: absolute;
  bottom: 5px;
  left: 0;
  display: none;
  height: 1rem;
  overflow: hidden;
  font-size: 0.7125rem;
  background-color: #f4f5fd;
  border-radius: 0.65rem;

  .dz-upload {
    background-color: #1bc943;
    transition: width 0.6s ease;
  }
}

::v-deep .dz-error-message {
  display: none;
}

::v-deep .dz-success-mark {
  display: none;
}

::v-deep .dz-error-mark {
  display: none;
}

::v-deep .dz-details {
  display: flex;
}

::v-deep .dz-size {
  display: none;
}

::v-deep .dz-image {
  display: none;
}

::v-deep .dz-cover-preview {
  flex-flow: column wrap;
  cursor: auto !important;
}

::v-deep .dz-cover-details {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  width: 100%;
}

::v-deep .dz-image-cover {
  width: 100%;
  height: 300px;
  margin-bottom: 1rem;
}

::v-deep *[data-dz-thumbnail-cover] {
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-size: contain;
  background-position: left;
}

::v-deep .dz-filename {
  display: flex;
  align-items: center;
  color: #7a7b97;
  gap: 10px;

  &::before {
    content: '';
    display: inline-block;
    width: 18px;
    height: 18px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 384 512'%3E%3Cpath d='M320 464c8.8 0 16-7.2 16-16l0-288-80 0c-17.7 0-32-14.3-32-32l0-80L64 48c-8.8 0-16 7.2-16 16l0 384c0 8.8 7.2 16 16 16l256 0zM0 64C0 28.7 28.7 0 64 0L229.5 0c17 0 33.3 6.7 45.3 18.7l90.5 90.5c12 12 18.7 28.3 18.7 45.3L384 448c0 35.3-28.7 64-64 64L64 512c-35.3 0-64-28.7-64-64L0 64z' fill='%233c44b1'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
  }
}

::v-deep .dz-remove {
  visibility: hidden;
  position: relative;
  text-align: right;
  right: -20px;

  &::after {
    content: '';
    display: block;
    width: 18px;
    height: 18px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 320 512'%3E%3Cpath fill='%23f83245' d='M310.6 361.4c12.5 12.5 12.5 32.75 0 45.25-6.2 6.25-14.4 9.35-22.6 9.35s-16.38-3.125-22.62-9.375L160 301.3 54.63 406.6C48.38 412.9 40.19 416 32 416s-16.37-3.1-22.625-9.4c-12.5-12.5-12.5-32.75 0-45.25l105.4-105.4L9.375 150.6c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L160 210.8l105.4-105.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-105.4 105.4L310.6 361.4z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    visibility: visible;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  &:hover::after {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 320 512'%3E%3Cpath fill='%23f3939d' d='M310.6 361.4c12.5 12.5 12.5 32.75 0 45.25-6.2 6.25-14.4 9.35-22.6 9.35s-16.38-3.125-22.62-9.375L160 301.3 54.63 406.6C48.38 412.9 40.19 416 32 416s-16.37-3.1-22.625-9.4c-12.5-12.5-12.5-32.75 0-45.25l105.4-105.4L9.375 150.6c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L160 210.8l105.4-105.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-105.4 105.4L310.6 361.4z'/%3E%3C/svg%3E");
  }
}
</style>
